import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSelectedStore } from '@/contexts/StoreContext';
import { useStore, useStores } from '@/hooks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@geely/ui";
import {
  LayoutDashboard,
  Store,
  Users,
  Clock,
  Calendar,
  Car,
  LogOut,
  Menu,
  X,
  Building2,
  MapPin,
  Phone,
  Mail,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { user, logout } = useAuth();
  const { selectedStoreId, setSelectedStoreId, userStoreIds, isMultiStore } = useSelectedStore();
  const { data: userStore } = useStore(selectedStoreId ?? '');
  const { data: allStores = [] } = useStores({ enabled: isMultiStore });
  const userStores = isMultiStore ? allStores.filter((s) => userStoreIds.includes(s.id)) : [];
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isStoreInfoOpen, setIsStoreInfoOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'store_admin'] },
    { path: '/admin/stores', label: 'Lojas', icon: Store, roles: ['admin'] },
    { path: '/admin/models', label: 'Modelos', icon: Car, roles: ['admin'] },
    { path: '/admin/users', label: 'Usuários', icon: Users, roles: ['admin'] },
    { path: '/admin/operating-hours', label: 'Horários', icon: Clock, roles: ['store_admin'] },
    { path: '/admin/appointments', label: 'Agendamentos', icon: Calendar, roles: ['store_admin'] },
  ];

  const filteredNavItems = navItems.filter((item) =>
    item.roles.includes(user?.role || '')
  );

  // Get store initials for avatar
  const getStoreInitials = (storeName: string) => {
    return storeName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 rounded-md hover:bg-gray-100"
              >
                {isMobileMenuOpen ? (
                  <X className="size-6" />
                ) : (
                  <Menu className="size-6" />
                )}
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                Agendamento Geely Care +
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">
                  {user?.role === 'admin' ? 'Administrador' : 'Admin da Loja'}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="size-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:block w-64 bg-white border-r border-gray-200 min-h-[calc(100vh-4rem)] sticky top-16">
          <div className="p-4 space-y-4">
            {/* Store Info Card - Only for store_admin */}
            {user?.role === 'store_admin' && userStore && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="flex-shrink-0 size-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-lg">
                    {getStoreInitials(userStore.name)}
                  </div>

                  {/* Store Name & Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm truncate">
                      {userStore.name}
                    </h3>
                    <p className="text-xs text-gray-600 truncate mt-0.5">
                      {userStore.dealerName}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 h-7 text-xs text-blue-700 hover:text-blue-800 hover:bg-blue-100 p-0"
                      onClick={() => setIsStoreInfoOpen(true)}
                    >
                      <Info className="size-3 mr-1" />
                      Ver detalhes
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Store Selector - Only for multi-store store_admin */}
            {user?.role === 'store_admin' && isMultiStore && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-500">Loja ativa</label>
                <Select value={selectedStoreId ?? ''} onValueChange={setSelectedStoreId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecione uma loja" />
                  </SelectTrigger>
                  <SelectContent>
                    {userStores.map((store) => (
                      <SelectItem key={store.id} value={store.id}>
                        {store.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Navigation Items */}
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                >
                  <Icon className="size-5" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </aside>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-16 bg-white z-40 p-4">
            <nav className="space-y-3">
              {/* Store Info Card - Mobile */}
              {user?.role === 'store_admin' && userStore && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200 mb-4">
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0 size-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-lg">
                      {getStoreInitials(userStore.name)}
                    </div>

                    {/* Store Name & Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-sm truncate">
                        {userStore.name}
                      </h3>
                      <p className="text-xs text-gray-600 truncate mt-0.5">
                        {userStore.dealerName}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 h-7 text-xs text-blue-700 hover:text-blue-800 hover:bg-blue-100 p-0"
                        onClick={() => {
                          setIsStoreInfoOpen(true);
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        <Info className="size-3 mr-1" />
                        Ver detalhes
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Store Selector - Mobile */}
              {user?.role === 'store_admin' && isMultiStore && (
                <div className="space-y-1.5 mb-4">
                  <label className="text-xs font-medium text-gray-500">Loja ativa</label>
                  <Select value={selectedStoreId ?? ''} onValueChange={setSelectedStoreId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Selecione uma loja" />
                    </SelectTrigger>
                    <SelectContent>
                      {userStores.map((store) => (
                        <SelectItem key={store.id} value={store.id}>
                          {store.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {filteredNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    <Icon className="size-5" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>

      {/* Store Details Dialog */}
      {userStore && (
        <Dialog open={isStoreInfoOpen} onOpenChange={setIsStoreInfoOpen}>
          <DialogContent className="max-w-2xl max-h-[calc(100vh-4rem)] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="size-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                  {getStoreInitials(userStore.name)}
                </div>
                Informações da Loja
              </DialogTitle>
              <DialogDescription>
                Detalhes completos da loja que você administra
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4 overflow-y-auto flex-1">
              {/* Basic Info */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Building2 className="size-4 text-blue-600" />
                  Informações Básicas
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Nome da Loja</p>
                    <p className="text-sm font-medium text-gray-900">{userStore.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Concessionária</p>
                    <p className="text-sm font-medium text-gray-900">{userStore.dealerName}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500 mb-1">Atendimentos Simultâneos</p>
                    <p className="text-sm font-medium text-gray-900">{userStore.parallelAppointments} agendamentos por horário</p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <MapPin className="size-4 text-blue-600" />
                  Endereço
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <p className="text-sm text-gray-900">
                    {userStore.street}, {userStore.number}
                  </p>
                  <p className="text-sm text-gray-900">
                    {userStore.neighborhood} - {userStore.city}
                  </p>
                  <p className="text-sm text-gray-900">
                    CEP: {userStore.zipCode}
                  </p>
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500">Coordenadas</p>
                    <p className="text-sm text-gray-700 font-mono">
                      {userStore.latitude}, {userStore.longitude}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Phone className="size-4 text-blue-600" />
                  Contato
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="size-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Telefone</p>
                      <p className="text-sm font-medium text-gray-900">{userStore.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="size-4 text-green-600" />
                    <div>
                      <p className="text-xs text-gray-500">WhatsApp</p>
                      <p className="text-sm font-medium text-gray-900">{userStore.whatsapp}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="size-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">E-mail</p>
                      <p className="text-sm font-medium text-gray-900">{userStore.email}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setIsStoreInfoOpen(false)}>
                Fechar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}